/**
 * Pure resolution of "after the pools" advancement tiers.
 *
 * Tier 1 is the existing `advancementPerPool → main draw`. Tiers 2..n come from
 * `qualifyingPools.tiers`; a legacy `qualifyingPlate` (Ultra Royale) is read as a
 * single tier 2 that keeps the `qualifying_plate` / `plate-` identifiers so
 * published draws still resolve. Nothing here touches the database.
 */
import type { AdvancementTier, DrawOptions } from '../types';

export interface ResolvedTier {
  index: number; // 1 = main draw
  id: string;
  name: string;
  drawType: 'elimination' | 'monrad';
  perPool: number; // after clamping to the positions that exist
  rankFrom: number; // 0-based pool rank, inclusive
  rankTo: number; // 0-based pool rank, inclusive
  stageId: string;
  matchPrefix: string; // prefix for match/round ids inside the tier's stages
  playoffPrefix: string; // prefix for monrad playoff stage ids inside the tier
}

/** Default names for tiers 2, 3, 4 … in order; past these a tier is "Tier <n>". The tier
 *  editor proposes them for a new tier, and a tier saved with a blank name falls back to
 *  the one for its position, so no stage is ever generated nameless. */
export const DEFAULT_TIER_NAMES = ['Plate', 'Bowl', 'Shield'] as const;

export function defaultTierName(index: number): string {
  return DEFAULT_TIER_NAMES[index - 2] ?? `Tier ${index}`;
}

/**
 * Tiers only exist where a pool produces a finishing order and the knockout is a plain
 * bracket: round-robin pools, no ladder template. Elimination pools (and a missing
 * `poolDrawType`, which the generator and the pools editor both treat as elimination)
 * keep the main tier alone — their winner reaches the main draw by a bracket feed, and
 * there is no standings order to seat a Bowl from. A ladder template seats its own
 * stage from standings, so no tier (main included) applies. This one check governs the
 * generator, the runtime seating and the editor alike.
 */
export function resolveAdvancementTiers(options: DrawOptions): ResolvedTier[] {
  const pools = options.qualifyingPools;
  if (!pools || pools.numPools <= 0) return [];
  if (options.ladderTemplate) return [];
  const standingsPools = pools.poolDrawType === 'round_robin';

  const playersPerPool = pools.playersPerPool;
  const mainPerPool = Math.min(pools.advancementPerPool, playersPerPool);
  const tiers: ResolvedTier[] = [
    {
      index: 1,
      id: 'main',
      name: 'Main Draw',
      drawType: 'elimination',
      perPool: mainPerPool,
      rankFrom: 0,
      rankTo: mainPerPool - 1,
      stageId: 'main',
      matchPrefix: '',
      playoffPrefix: '',
    },
  ];

  type Configured = AdvancementTier & { legacy?: boolean };
  const configured: Configured[] = !standingsPools
    ? []
    : (pools.tiers ??
      (options.qualifyingPlate
        ? [
            {
              id: 'tier_2',
              name: 'Plate',
              perPool: playersPerPool - mainPerPool,
              // Legacy plate only special-cases 'monrad' (see drawGenerator.ts /
              // matchProcessor.ts) — anything else runs as elimination.
              drawType: options.qualifyingPlate.drawType === 'monrad' ? 'monrad' : 'elimination',
              legacy: true,
            },
          ]
        : []));

  let rankFrom = mainPerPool;
  configured.forEach((tier, i) => {
    const remaining = playersPerPool - rankFrom;
    const perPool = Math.min(tier.perPool, remaining);
    if (perPool <= 0) return;
    const index = i + 2;
    const legacy = tier.legacy === true;
    tiers.push({
      index,
      id: tier.id,
      name: tier.name.trim() || defaultTierName(index),
      drawType: tier.drawType,
      perPool,
      rankFrom,
      rankTo: rankFrom + perPool - 1,
      stageId: legacy ? 'qualifying_plate' : `tier_${index}`,
      matchPrefix: legacy ? 'plate-' : `t${index}-`,
      playoffPrefix: legacy ? 'plate_' : `t${index}_`,
    });
    rankFrom += perPool;
  });

  return tiers;
}

/** Bracket size for a tier: next power of two that fits every pool's positions. */
export function tierDrawSize(tier: Pick<ResolvedTier, 'perPool'>, numPools: number): number {
  const entrants = numPools * tier.perPool;
  if (entrants <= 1) return 2;
  return Math.pow(2, Math.ceil(Math.log2(entrants)));
}

/**
 * The pool (0-based) the `i`-th qualifying player is snake-drafted into: left to right,
 * then right to left, and so on. The one definition of the draft — the generator deals
 * players with it and `poolSizes` counts with it, so the two can never disagree.
 */
export function snakePoolIndex(i: number, numPools: number): number {
  const cycle = i % (numPools * 2);
  return cycle < numPools ? cycle : numPools * 2 - 1 - cycle;
}

/**
 * The real size of every pool (index 0 = pool 1) when `qualifyingPlayerCount` players are
 * snake-drafted into `numPools` pools. Uneven counts leave some pools one short — which
 * pools depends on the draft direction of the last, partial round (10 into 3 → 3/3/4).
 * Seat plans and placement bands are built from these, never from the largest pool alone,
 * so a short pool is never given a seat for a finishing position it does not have.
 */
export function poolSizes(qualifyingPlayerCount: number, numPools: number): number[] {
  if (numPools <= 0) return [];
  const sizes = new Array<number>(numPools).fill(0);
  for (let i = 0; i < qualifyingPlayerCount; i++) sizes[snakePoolIndex(i, numPools)] += 1;
  return sizes;
}

/**
 * The options a draw is actually generated (and saved) with: `playersPerPool` is the
 * real size of the largest pool, `ceil(qualifying players / numPools)` — pools are
 * snake-drafted from the registrant list, so that is what exists whatever a preset
 * (Ultra Royale hard-codes 4) or a form value from before registrations changed says.
 * Tier ranks, sizes and bands all hang off it, so the generator and the runtime must
 * read the same number. Returns the input unchanged when there is nothing to derive.
 */
export function withDerivedPlayersPerPool(
  options: DrawOptions,
  qualifyingPlayerCount: number,
): DrawOptions {
  const pools = options.qualifyingPools;
  if (!pools || pools.numPools <= 0 || qualifyingPlayerCount <= 0) return options;
  const playersPerPool = Math.max(...poolSizes(qualifyingPlayerCount, pools.numPools));
  if (pools.playersPerPool === playersPerPool) return options;
  return { ...options, qualifyingPools: { ...pools, playersPerPool } };
}
